import type { FoodCategory, MealCategory, TimePeriod, TimeRange, Unit } from '@/types/nutrition'
import { z } from 'zod'

/**
 * Zod schema for runtime validation of nutrition data.
 * Ensures data integrity when loading from localStorage/IndexedDB.
 *
 * @module nutritionSchema
 */

// ============================================================================
// Base Schemas - Branded Types & Units
// ============================================================================

/**
 * Schema for Calories (non-negative number)
 * Uses Zod's brand() for type safety matching our branded Calories type
 */
export const CaloriesSchema = z.number().nonnegative().brand('Calories')

/**
 * Schema for Grams (non-negative number)
 * Uses Zod's brand() for type safety matching our branded Grams type
 */
export const GramsSchema = z.number().nonnegative().brand('Grams')

/**
 * Schema for Milligrams (non-negative number)
 * Uses Zod's brand() for type safety matching our branded Milligrams type
 */
export const MilligramsSchema = z.number().nonnegative().brand('Milligrams')

/**
 * Schema for measurement/volume units
 */
export const UnitSchema = z.enum(['g', 'ml', 'piece']) as z.ZodType<Unit>

/**
 * Schema for food categories
 */
export const FoodCategorySchema = z.enum([
  'Fruits and Vegetables',
  'Legumes',
  'Whole Grains',
  'Nuts and Seeds',
  'Root Vegetables',
  'Herbs and Spices',
  'Meat, Poultry, Fish & Seafood',
  'Dairy & Eggs',
  'Fats, Oils & Spreads',
  'Processed Foods & Snacks',
  'Sweets, Sugars & Desserts',
  'Supplements & Protein Powders',
  'Beverages',
  'Unknown',
]) as z.ZodType<FoodCategory>

/**
 * Schema for meal categories
 */
export const MealCategorySchema = z.enum([
  'Breakfast',
  'Lunch',
  'Dinner',
  'Late meal',
]) as z.ZodType<MealCategory>

/**
 * Schema for time ranges (hourly distribution)
 */
export const TimeRangeSchema = z.enum([
  '00-04',
  '04-08',
  '08-12',
  '12-16',
  '16-20',
  '20-24',
]) as z.ZodType<TimeRange>

/**
 * Schema for time periods (filtering)
 */
export const TimePeriodSchema = z.enum([
  'week',
  'month',
  '3months',
  '6months',
  'year',
  'custom',
]) as z.ZodType<TimePeriod>

// ============================================================================
// Core Data Structures
// ============================================================================

/**
 * Schema for nutritional values
 */
export const NutritionalValueSchema = z.object({
  calories: CaloriesSchema,
  carbs: GramsSchema,
  fat: GramsSchema,
  protein: GramsSchema,
  cholesterol: MilligramsSchema,
  sodium: MilligramsSchema,
  sugar: GramsSchema,
  fiber: GramsSchema,
})

/**
 * Schema for quantity with unit
 */
export const QuantitySchema = z.object({
  amount: z.number().nonnegative(),
  unit: UnitSchema,
})

/**
 * Schema for food items
 */
export const FoodItemSchema = z.object({
  name: z.string().min(1, 'Food name is required'),
  quantity: QuantitySchema,
  nutrition: NutritionalValueSchema,
  category: FoodCategorySchema,
})

/**
 * Schema for meals
 */
export const MealSchema = z.object({
  category: z.string().min(1, 'Meal category is required'),
  foods: z.array(FoodItemSchema).readonly(),
})

/**
 * Schema for date validation (converts string to Date)
 */
export const DateSchema = z.union([
  z.date(),
  z.string().transform((val) => {
    const date = new Date(val)
    if (Number.isNaN(date.getTime())) {
      throw new Error(`Invalid date: ${val}`)
    }
    return date
  }),
])

/**
 * Schema for daily nutrition entries
 */
export const DailyNutritionSchema = z.object({
  date: DateSchema,
  meals: z.array(MealSchema).readonly(),
  totals: NutritionalValueSchema,
})

/**
 * Schema for array of daily nutrition entries (main storage format)
 */
export const DailyNutritionArraySchema = z.array(DailyNutritionSchema)

// ============================================================================
// Statistics & Analysis Schemas
// ============================================================================

/**
 * Schema for product statistics
 */
export const ProductStatisticsSchema = z.object({
  product: z.string(),
  totalCalories: CaloriesSchema,
  totalWeight: GramsSchema,
  totalVolume: z.number().nonnegative(), // Milliliters (not branded in stats)
  totalPieces: z.number().nonnegative(),
  frequency: z.number().int().nonnegative(),
  avgPortion: z.number().nonnegative(),
  avgPortionUnit: UnitSchema,
  proteins: GramsSchema,
  fats: GramsSchema,
  carbohydrates: GramsSchema,
  fiber: GramsSchema,
})

/**
 * Schema for time-based calories
 */
export const TimeBasedCaloriesSchema = z.object({
  date: DateSchema,
  timeRanges: z.record(z.string(), CaloriesSchema), // Record with string keys
  totalCalories: CaloriesSchema,
})

/**
 * Schema for nutrition goals
 */
export const NutritionGoalsSchema = z.object({
  dailyCalories: CaloriesSchema,
  macroTargets: z.object({
    carbsPercent: z.number().min(0).max(100),
    fatPercent: z.number().min(0).max(100),
    proteinPercent: z.number().min(0).max(100),
  }),
})

/**
 * Schema for daily nutrition summary (for charts)
 */
export const DailyNutritionSummarySchema = z.object({
  date: DateSchema,
  dayOfWeek: z.string(),
  nutrition: NutritionalValueSchema,
  macroPercentages: z.object({
    carbsPercent: z.number().min(0).max(100),
    fatPercent: z.number().min(0).max(100),
    proteinPercent: z.number().min(0).max(100),
  }),
  timeBasedCalories: z.record(z.string(), CaloriesSchema), // Record with string keys
})

// ============================================================================
// Validation Helper Functions
// ============================================================================

/**
 * Validates and parses nutrition data from unknown source.
 * Returns validated data or throws validation error.
 *
 * @param data - Unknown data to validate
 * @returns Validated DailyNutrition array
 * @throws {z.ZodError} If validation fails
 *
 * @example
 * ```typescript
 * try {
 *   const data = validateNutritionData(rawData)
 *   // Safe to use - TypeScript knows the shape
 * } catch (error) {
 *   if (error instanceof z.ZodError) {
 *     logger.error('Invalid nutrition data', error)
 *   }
 * }
 * ```
 */
export function validateNutritionData(data: unknown) {
  return DailyNutritionArraySchema.parse(data)
}

/**
 * Safely validates nutrition data without throwing.
 * Returns validation result with success flag.
 *
 * @param data - Unknown data to validate
 * @returns Validation result with data or error
 *
 * @example
 * ```typescript
 * const result = safeValidateNutritionData(rawData)
 * if (result.success) {
 *   console.log(result.data) // Validated data
 * } else {
 *   console.error(result.error.errors) // Validation errors
 * }
 * ```
 */
export function safeValidateNutritionData(data: unknown) {
  return DailyNutritionArraySchema.safeParse(data)
}

/**
 * Validates a single daily nutrition entry.
 *
 * @param data - Unknown data to validate
 * @returns Validated DailyNutrition entry
 * @throws {z.ZodError} If validation fails
 */
export function validateDailyNutritionEntry(data: unknown) {
  return DailyNutritionSchema.parse(data)
}

/**
 * Safely validates a single daily nutrition entry without throwing.
 *
 * @param data - Unknown data to validate
 * @returns Validation result with data or error
 */
export function safeValidateDailyNutritionEntry(data: unknown) {
  return DailyNutritionSchema.safeParse(data)
}

/**
 * Validates nutrition goals data.
 *
 * @param data - Unknown data to validate
 * @returns Validated NutritionGoals
 * @throws {z.ZodError} If validation fails
 */
export function validateNutritionGoals(data: unknown) {
  return NutritionGoalsSchema.parse(data)
}

/**
 * Safely validates nutrition goals without throwing.
 *
 * @param data - Unknown data to validate
 * @returns Validation result with data or error
 */
export function safeValidateNutritionGoals(data: unknown) {
  return NutritionGoalsSchema.safeParse(data)
}

/**
 * Type guard that checks if unknown data is valid nutrition data.
 * Useful for runtime type checking without exceptions.
 *
 * @param data - Unknown data to check
 * @returns True if data is valid DailyNutrition array
 *
 * @example
 * ```typescript
 * if (isValidNutritionData(data)) {
 *   // TypeScript narrows type to DailyNutrition[]
 *   data.forEach(entry => console.log(entry.date))
 * }
 * ```
 */
export function isValidNutritionData(
  data: unknown
): data is z.infer<typeof DailyNutritionArraySchema> {
  return DailyNutritionArraySchema.safeParse(data).success
}

/**
 * Type guard for single nutrition entry.
 *
 * @param data - Unknown data to check
 * @returns True if data is valid DailyNutrition entry
 */
export function isValidDailyNutritionEntry(
  data: unknown
): data is z.infer<typeof DailyNutritionSchema> {
  return DailyNutritionSchema.safeParse(data).success
}

/**
 * Partial validation - validates only the fields present.
 * Useful for incremental updates or partial data.
 *
 * @param data - Partial nutrition data to validate
 * @returns Validated partial data
 */
export function validatePartialNutritionEntry(data: unknown) {
  return DailyNutritionSchema.partial().parse(data)
}

/**
 * Gets detailed validation errors in a user-friendly format.
 *
 * @param data - Data that failed validation
 * @returns Array of error messages
 *
 * @example
 * ```typescript
 * const errors = getValidationErrors(invalidData)
 * errors.forEach(error => console.error(error))
 * // Output: "calories: Expected number, received string"
 * ```
 */
export function getValidationErrors(data: unknown): string[] {
  const result = DailyNutritionArraySchema.safeParse(data)
  if (result.success) return []

  return result.error.issues.map((err) => `${err.path.join('.')}: ${err.message}`)
}

/**
 * Sanitizes nutrition data by removing invalid entries.
 * Useful for recovering from corrupted storage data.
 *
 * @param data - Array of entries to sanitize
 * @returns Sanitized array with only valid entries
 *
 * @example
 * ```typescript
 * const cleaned = sanitizeNutritionData(rawData)
 * logger.info(`Removed ${rawData.length - cleaned.length} invalid entries`)
 * ```
 */
export function sanitizeNutritionData(data: unknown[]): ValidatedDailyNutrition[] {
  return data
    .map((entry) => safeValidateDailyNutritionEntry(entry))
    .filter((result) => result.success)
    .map((result) => result.data)
}

// ============================================================================
// Type Exports (for convenience)
// ============================================================================

export type ValidatedDailyNutrition = z.infer<typeof DailyNutritionSchema>
export type ValidatedNutritionArray = z.infer<typeof DailyNutritionArraySchema>
export type ValidatedNutritionalValue = z.infer<typeof NutritionalValueSchema>
export type ValidatedFoodItem = z.infer<typeof FoodItemSchema>
export type ValidatedMeal = z.infer<typeof MealSchema>
export type ValidatedNutritionGoals = z.infer<typeof NutritionGoalsSchema>
